import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import useApi from '../hooks/useApi';
import MenuHeader from './MenuHeader';
import useAuth from '../hooks/useAuth';
import AuthService from '../services/auth.service';
import PaymentService from '../services/payment.service';
import { useNotifications } from '../context/NotificationProvider';
import { useTranslation } from 'react-i18next';

const ADD_ICON_PATH = 'M0 8.85266C0 4.44641 3.5625 0.852661 8 0.852661C12.4062 0.852661 16 4.44641 16 8.85266C16 13.2902 12.4062 16.8527 8 16.8527C3.5625 16.8527 0 13.2902 0 8.85266ZM8 12.3527C8.40625 12.3527 8.75 12.0402 8.75 11.6027V9.60266H10.75C11.1562 9.60266 11.5 9.29016 11.5 8.85266C11.5 8.44641 11.1562 8.10266 10.75 8.10266H8.75V6.10266C8.75 5.69641 8.40625 5.35266 8 5.35266C7.5625 5.35266 7.25 5.69641 7.25 6.10266V8.10266H5.25C4.8125 8.10266 4.5 8.44641 4.5 8.85266C4.5 9.29016 4.8125 9.60266 5.25 9.60266H7.25V11.6027C7.25 12.0402 7.5625 12.3527 8 12.3527Z';

const PaymentCards = () => {
  const { t } = useTranslation('payment');
  const api = useApi();
  const { auth } = useAuth();
  const notify = useNotifications();

  const theme = createTheme({
    palette: {
      primary: {
        main: 'rgba(57, 61, 68, 1)',
      },
      secondary: {
        main: 'rgba(250, 239, 155, 1)',
      },
    },
  });

  const apiRef = useRef(api);
  const notifyRef = useRef(notify);
  const tRef = useRef(t);
  const walleeScriptRef = useRef(null);
  const cardsRequestRef = useRef(null);
  const processedReturnRef = useRef(false);
  const submitTimeoutRef = useRef(null);

  const userId = auth.user?.id ?? null;

  const [cards, setCards] = useState([]);
  const [handler, setHandler] = useState(null);
  const [isPreparingCard, setIsPreparingCard] = useState(false);
  const [isSecureFrameReady, setIsSecureFrameReady] = useState(false);
  const [secureFrameHeight, setSecureFrameHeight] = useState(null);
  const [isSubmittingCard, setIsSubmittingCard] = useState(false);
  const [primaryAction, setPrimaryAction] = useState({ label: null, trigger: null });
  const [pendingDeleteCard, setPendingDeleteCard] = useState(null);
  const [deletingToken, setDeletingToken] = useState(null);

  const isCardSetupActive = isPreparingCard || handler !== null;

  useEffect(() => {
    apiRef.current = api;
  }, [api]);

  useEffect(() => {
    notifyRef.current = notify;
  }, [notify]);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const clearSubmitTimeout = useCallback(() => {
    if (submitTimeoutRef.current) {
      window.clearTimeout(submitTimeoutRef.current);
      submitTimeoutRef.current = null;
    }
  }, []);

  const markSubmitting = useCallback(() => {
    setIsSubmittingCard(true);
    clearSubmitTimeout();
    submitTimeoutRef.current = window.setTimeout(() => {
      setIsSubmittingCard(false);
      submitTimeoutRef.current = null;
    }, 12000);
  }, [clearSubmitTimeout]);

  const paymentMethodConfigurationIdFrom = (payload) => {
    const raw =
      payload?.payment_method_configuration_id ??
      payload?.paymentMethodConfigurationId ??
      payload?.payment_method_configuration_ids?.[0] ??
      payload?.paymentMethodConfigurationIds?.[0];
    const parsed = Number(raw);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };

  const removeWalleeScript = useCallback(() => {
    if (walleeScriptRef.current?.parentNode) {
      walleeScriptRef.current.parentNode.removeChild(walleeScriptRef.current);
    }
    walleeScriptRef.current = null;
  }, []);

  const resetWalleeFrame = useCallback(() => {
    const iframeContainer = document.getElementById('wallee-iframe');
    if (iframeContainer) {
      iframeContainer.innerHTML = '';
    }
  }, []);

  const resetCardSetup = useCallback(() => {
    removeWalleeScript();
    resetWalleeFrame();
    clearSubmitTimeout();
    setHandler(null);
    setIsPreparingCard(false);
    setIsSecureFrameReady(false);
    setSecureFrameHeight(null);
    setIsSubmittingCard(false);
    setPrimaryAction({ label: null, trigger: null });
  }, [clearSubmitTimeout, removeWalleeScript, resetWalleeFrame]);

  const fetchCards = useCallback(async (targetUserId = userId) => {
    if (!targetUserId) {
      return;
    }

    if (cardsRequestRef.current?.userId === targetUserId) {
      return cardsRequestRef.current.promise;
    }

    const request = AuthService
      .me({ id: targetUserId, with: 'cards.brand' }, apiRef.current, { showLoader: false })
      .then((response) => {
        setCards(response?.user?.cards ?? []);
      })
      .catch((err) => {
        console.error(err);
        notifyRef.current.error(
          tRef.current('cards_load_failed', {
            defaultValue: 'Unable to load your cards. Please try again.',
          }),
        );
      })
      .finally(() => {
        if (cardsRequestRef.current?.promise === request) {
          cardsRequestRef.current = null;
        }
      });

    cardsRequestRef.current = {
      userId: targetUserId,
      promise: request,
    };

    return request;
  }, [userId]);

  const checkSuccessParamInUrl = useCallback(async (targetUserId) => {
    const urlParams = new URLSearchParams(window.location.search);
    const transactionId = urlParams.get('transactionId');
    const hasReturnParam = urlParams.has('success') || urlParams.has('failed');

    if (!hasReturnParam || processedReturnRef.current) {
      return false;
    }

    processedReturnRef.current = true;
    window.history.replaceState({}, document.title, window.location.pathname);

    if (urlParams.has('success')) {
      try {
        if (transactionId) {
          await PaymentService.syncTokenTransaction(transactionId, apiRef.current, { showLoader: false });
        }
        await fetchCards(targetUserId);
        notifyRef.current.success(tRef.current('card_added_success'));
      } catch (err) {
        console.warn('Card sync after Wallee return failed:', err);
        await fetchCards(targetUserId);
        notifyRef.current.error(
          tRef.current('card_sync_unavailable', {
            defaultValue: 'The card was verified, but we could not refresh it automatically.',
          }),
        );
      }
      return true;
    }

    notifyRef.current.error(
      tRef.current('card_setup_failed', {
        defaultValue: 'The card was not added. Please try again.',
      }),
    );
    return true;
  }, [fetchCards]);

  useEffect(() => {
    if (!userId) {
      return undefined;
    }

    let cancelled = false;

    void (async () => {
      const handledReturn = await checkSuccessParamInUrl(userId);
      if (!cancelled && !handledReturn) {
        await fetchCards(userId);
      }
    })();

    return () => {
      cancelled = true;
      resetCardSetup();
    };
  }, [checkSuccessParamInUrl, fetchCards, resetCardSetup, userId]);

  const notifyCardSetupError = useCallback(() => {
    notifyRef.current.error(
      tRef.current('card_setup_unavailable', {
        defaultValue: 'Unable to load the secure card form. Please try again.',
      }),
    );
  }, []);

  const primaryActionLabelFrom = useCallback((args) => {
    const rawLabel = args.find((arg) => typeof arg === 'string')?.trim() ?? '';

    if (!rawLabel || /^(pay|payer)\b/i.test(rawLabel)) {
      return tRef.current('continue_card_setup', { defaultValue: 'Continue' });
    }

    return rawLabel;
  }, []);

  const loadIframe = (paymentIframeScript, paymentMethodConfigurationId) => {
    const script = document.createElement('script');
    script.src = paymentIframeScript;
    script.async = true;
    walleeScriptRef.current = script;

    const timeout = window.setTimeout(() => {
      setIsPreparingCard(false);
      setHandler(null);
      setIsSecureFrameReady(false);
      setSecureFrameHeight(null);
      notifyCardSetupError();
    }, 18000);

    script.onload = () => {
      window.clearTimeout(timeout);
      try {
        if (!window.IframeCheckoutHandler) {
          throw new Error('Wallee iframe handler was not loaded.');
        }

        window.IframeCheckoutHandler.configure('replacePrimaryAction', true);
        const iframeHandler = paymentMethodConfigurationId
          ? window.IframeCheckoutHandler(paymentMethodConfigurationId)
          : window.IframeCheckoutHandler();
        setHandler(iframeHandler);
      } catch (err) {
        console.error(err);
        setHandler(null);
        setIsSecureFrameReady(false);
        setSecureFrameHeight(null);
        notifyCardSetupError();
      } finally {
        setIsPreparingCard(false);
      }
    };

    script.onerror = () => {
      window.clearTimeout(timeout);
      setIsPreparingCard(false);
      setHandler(null);
      setIsSecureFrameReady(false);
      setSecureFrameHeight(null);
      notifyCardSetupError();
    };

    document.body.appendChild(script);
  };

  const addCard = async () => {
    if (isCardSetupActive) {
      return;
    }

    setIsPreparingCard(true);
    setIsSecureFrameReady(false);
    setPrimaryAction({ label: null, trigger: null });
    removeWalleeScript();
    resetWalleeFrame();

    try {
      const response = await PaymentService.createTokenTransaction(apiRef.current, {
        return_url: window.location.origin,
      }, { showLoader: false });
      const payload = response?.data ?? {};
      const paymentIframe = payload.payment_iframe;
      const paymentMethodConfigurationId = paymentMethodConfigurationIdFrom(payload);

      if (!paymentIframe) {
        throw new Error('Missing Wallee card setup iframe URL.');
      }

      loadIframe(paymentIframe, paymentMethodConfigurationId);
    } catch (err) {
      console.error(err);
      setIsPreparingCard(false);
      notifyCardSetupError();
    }
  };

  useEffect(() => {
    if (!handler) {
      return undefined;
    }

    try {
      setIsSecureFrameReady(false);
      setSecureFrameHeight(null);

      if (typeof handler.setReplacePrimaryActionCallback === 'function') {
        handler.setReplacePrimaryActionCallback((first, second) => {
          const args = [first, second];
          const trigger = args.find((arg) => typeof arg === 'function') ?? null;

          setPrimaryAction({
            label: primaryActionLabelFrom(args),
            trigger,
          });
        });
      }

      if (typeof handler.setResetPrimaryActionCallback === 'function') {
        handler.setResetPrimaryActionCallback(() => {
          setPrimaryAction({ label: null, trigger: null });
          setIsSubmittingCard(false);
        });
      }

      if (typeof handler.setHeightChangeCallback === 'function') {
        handler.setHeightChangeCallback((height) => {
          const nextHeight = Number(height);
          if (Number.isFinite(nextHeight) && nextHeight > 0) {
            setSecureFrameHeight(Math.max(430, Math.ceil(nextHeight)));
          }
        });
      }

      handler.setValidationCallback((validationResult) => {
        if (validationResult.success) {
          markSubmitting();
          try {
            handler.submit();
          } catch (err) {
            console.error(err);
            clearSubmitTimeout();
            setIsSubmittingCard(false);
            notifyCardSetupError();
          }
          return;
        }

        setIsSubmittingCard(false);
        const errors = Array.isArray(validationResult.errors)
          ? validationResult.errors
          : [tRef.current('card_setup_failed', { defaultValue: 'The card was not added. Please try again.' })];
        errors.forEach((error) => {
          notifyRef.current.error(typeof error === 'string' ? error : error?.message ?? String(error));
        });
      });

      handler.create('wallee-iframe');

      const frameContainer = document.getElementById('wallee-iframe');
      let readyIframe = null;

      const getSecureFrame = () => frameContainer?.querySelector('iframe') ?? null;
      const markFrameReady = () => {
        const iframe = getSecureFrame();
        if (!iframe) {
          return false;
        }

        const rect = iframe.getBoundingClientRect();
        if (rect.width > 120 && rect.height > 120) {
          setIsSecureFrameReady(true);
          return true;
        }

        return false;
      };

      const bindFrameLoad = () => {
        const iframe = getSecureFrame();
        if (!iframe || readyIframe === iframe) {
          return;
        }

        readyIframe = iframe;
        iframe.setAttribute('tabindex', '0');
        iframe.addEventListener('load', markFrameReady);
      };

      bindFrameLoad();
      markFrameReady();

      const observer = new MutationObserver(() => {
        bindFrameLoad();
        if (markFrameReady()) {
          observer.disconnect();
        }
      });

      if (frameContainer) {
        observer.observe(frameContainer, { childList: true, subtree: true });
      }

      const visibilityCheck = window.setInterval(() => {
        bindFrameLoad();
        if (markFrameReady()) {
          window.clearInterval(visibilityCheck);
          observer.disconnect();
        }
      }, 140);

      const readyTimeout = window.setTimeout(() => {
        if (!markFrameReady()) {
          setHandler(null);
          setIsSecureFrameReady(false);
          setSecureFrameHeight(null);
          notifyCardSetupError();
        }
        window.clearInterval(visibilityCheck);
        observer.disconnect();
      }, 25000);

      return () => {
        window.clearTimeout(readyTimeout);
        window.clearInterval(visibilityCheck);
        if (readyIframe) {
          readyIframe.removeEventListener('load', markFrameReady);
        }
        observer.disconnect();
      };
    } catch (err) {
      console.error(err);
      setHandler(null);
      setIsSecureFrameReady(false);
      setSecureFrameHeight(null);
      setIsSubmittingCard(false);
      notifyCardSetupError();
    }

    return undefined;
  }, [clearSubmitTimeout, handler, markSubmitting, notifyCardSetupError, primaryActionLabelFrom]);

  const confirmAddCard = () => {
    if (!handler || isSubmittingCard) {
      return;
    }

    const replacedActionTrigger = primaryAction.trigger
      || (primaryAction.label && typeof handler.trigger === 'function' ? () => handler.trigger() : null);

    if (replacedActionTrigger) {
      markSubmitting();
      try {
        replacedActionTrigger();
      } catch (err) {
        console.error(err);
        clearSubmitTimeout();
        setIsSubmittingCard(false);
        notifyCardSetupError();
      }
      return;
    }

    handler.validate();
  };

  const cancelSetup = async () => {
    resetCardSetup();
    await fetchCards(userId);
  };

  const confirmDeleteCard = async () => {
    if (!pendingDeleteCard || deletingToken !== null) {
      return;
    }

    const token = pendingDeleteCard.token;
    setDeletingToken(token);

    try {
      await PaymentService.deleteCard(token, apiRef.current, { showLoader: false });
      notifyRef.current.success(tRef.current('card_deleted_success'));
      setPendingDeleteCard(null);
      await fetchCards(userId);
    } catch (err) {
      console.error(err);
      notifyRef.current.error(
        tRef.current('card_delete_failed', {
          defaultValue: 'Unable to delete this card. Please try again.',
        }),
      );
    } finally {
      setDeletingToken(null);
    }
  };

  const primaryButtonText = isSubmittingCard
    ? t('saving_card', { defaultValue: 'Saving...' })
    : (!handler || !isSecureFrameReady)
      ? t('preparing_card_cta', { defaultValue: 'Preparing...' })
    : (primaryAction.label || t('save_card', { defaultValue: 'Save card' }));

  const secureFrameStyle = secureFrameHeight
    ? { '--wallee-frame-min-height': `${secureFrameHeight}px` }
    : undefined;

  return (
    <>
      <MenuHeader />

      <div className={`container container-payment-card${isCardSetupActive ? ' container-payment-card--setup' : ''}`}>
        <h2>{t('payment_cards')}</h2>

        {!isCardSetupActive && (
          <div className="cards">
            {cards.length > 0 ? cards.map((card) => (
              <div key={card.id || card.token} className="payment-card payment-card--managed">
                <div className="flex-column gap-10">
                  <div className="flex-row space-between align-center">
                    <img
                      className="brand-icon"
                      src={card.brand?.logo}
                      alt={card.brand?.name || 'card-brand'}
                      width={45}
                    />
                    <IconButton
                      onClick={() => setPendingDeleteCard(card)}
                      disabled={deletingToken === card.token}
                      aria-label={t('delete_card')}
                    >
                      <img src="assets/images/icon-trash.svg" alt={t('delete_card')} />
                    </IconButton>
                  </div>
                  <div className="flex-column">
                    <span className="card-holder">{card.card_holder}</span>
                    <span className="hashed">{String(card.hashed ?? '').replaceAll('X', '•')}</span>
                    <span className="expiry">
                      {t('expire')} {new Date(card.expiry).toLocaleDateString('en-US', { month: '2-digit', year: '2-digit' })}
                    </span>
                  </div>
                </div>
              </div>
            )) : (
              <div className="payment-card-empty">
                <strong>{t('no_saved_cards', { defaultValue: 'No saved cards yet' })}</strong>
                <span>{t('no_saved_cards_helper', { defaultValue: 'Add a card to pay faster for your next booking.' })}</span>
              </div>
            )}
          </div>
        )}

        {isCardSetupActive && (
          <div className="card-setup-panel">
            <div className="card-setup-header">
              <strong>{t('secure_card_setup_title', { defaultValue: 'Ajouter une carte sécurisée' })}</strong>
              {/* <span>{t('secure_card_setup_helper', { defaultValue: 'Vos cartes restent protégées par Wallee.' })}</span> */}
            </div>

            {!isSecureFrameReady && (
              <div className="card-setup-status" role="status" aria-live="polite">
                <span className="card-setup-status__spinner" />
                <div>
                  <strong>
                    {handler === null
                      ? t('preparing_secure_card_form', { defaultValue: 'Préparation du formulaire sécurisé' })
                      : t('loading_secure_card_form', { defaultValue: 'Chargement du formulaire sécurisé' })}
                  </strong>
                  <span>
                    {t('secure_card_form_helper', { defaultValue: 'Vos cartes restent protégées par notre système sécurisé.' })}
                  </span>
                </div>
              </div>
            )}

            {handler !== null && (
              <div className="wallee-frame-shell" style={secureFrameStyle}>
                <div id="wallee-iframe" style={secureFrameStyle} />
              </div>
            )}
          </div>
        )}

        <ThemeProvider theme={theme}>
          {!isCardSetupActive ? (
            <Button
              className="payment-card-primary-btn"
              onClick={addCard}
              variant="outlined"
              sx={{ width: '100%', maxWidth: '214px', margin: '0 auto', borderRadius: '15px', border: '2px solid rgba(57, 61, 68, 1)', alignItems: 'center', fontFamily: 'Lato !important', gap: '10px', textTransform: 'none', lineHeight: 1, padding: '15px 20px' }}
            >
              <svg width="16" height="17" viewBox="0 0 16 17" fill="#393D44" xmlns="http://www.w3.org/2000/svg">
                <path d={ADD_ICON_PATH} />
              </svg>
              {t('add_card')}
            </Button>
          ) : (
            <Box className="payment-card-setup-actions">
              <Button
                className="payment-card-primary-btn"
                onClick={confirmAddCard}
                disabled={!handler || !isSecureFrameReady || isSubmittingCard}
                variant="contained"
                sx={{ width: '100%', maxWidth: '240px', margin: '0 auto', borderRadius: '15px', alignItems: 'center', fontFamily: 'Lato !important', gap: '10px', textTransform: 'none', lineHeight: 1, padding: '15px 20px', color: 'rgba(250, 239, 155, 1)' }}
              >
                <svg width="16" height="17" viewBox="0 0 16 17" fill="rgba(250, 239, 155, 1)" xmlns="http://www.w3.org/2000/svg">
                  <path d={ADD_ICON_PATH} />
                </svg>
                {primaryButtonText}
              </Button>
              <Button
                onClick={cancelSetup}
                variant="text"
                sx={{ width: '100%', maxWidth: '240px', margin: '0 auto', borderRadius: '15px', alignItems: 'center', fontFamily: 'Lato !important', gap: '10px', textTransform: 'none', lineHeight: 1, padding: '15px 20px' }}
              >
                {t('cancel_card_setup', { defaultValue: 'Cancel setup' })}
              </Button>
            </Box>
          )}
        </ThemeProvider>
      </div>

      <Dialog
        open={pendingDeleteCard !== null}
        onClose={() => {
          if (deletingToken === null) {
            setPendingDeleteCard(null);
          }
        }}
        PaperProps={{ className: 'payment-card-delete-dialog' }}
      >
        <DialogTitle>{t('delete_card_title', { defaultValue: 'Delete this card?' })}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('delete_card_message', {
              defaultValue: 'This card will be removed from your Flyspa account. Existing orders are not affected.',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPendingDeleteCard(null)}
            disabled={deletingToken !== null}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={confirmDeleteCard}
            disabled={deletingToken !== null}
            color="error"
            variant="contained"
          >
            {deletingToken !== null
              ? t('deleting_card', { defaultValue: 'Deleting...' })
              : t('delete_card_confirm', { defaultValue: 'Delete card' })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PaymentCards;
